import moment from "moment"
import { Params } from "react-router-dom"
import { Dispatch } from "redux"
import storage from "redux-persist/lib/storage"
import { GetUserapiAdminTestsGetTestByIdByTestIdApiResponse } from "../../app/services/meseeknaApi"
import { environment, simLogos } from "../../environments/environment"
import { resetDecisions } from "../simulation-decisions/decisionsSlice"
import {
  Choice,
  ChoiceType,
  DecisionWithExtraType,
} from "../simulation-decisions/types"
import {
  MessagesWithExtraType,
  MessageTypes,
} from "../simulation-messages/types"
import { destroyTimer, resetSimulation } from "./simulationSlice"
import {
  Contents,
  Event,
  Information,
  InformationType,
  InformationWithExtraType,
  Response,
  SavePointLogType,
  Simulation,
  SimulationState,
} from "./types"

// Method to convert HH:MM:SS to seconds
export const convertToSeconds = (time: string): number => {
  if (time === "") return 0
  // no trailing numbers, ie 12 or 5 or 8 (assume minutes)
  if (time.length === 1) time = `00:0${time}:00`
  if (time.length === 2) time = `00:${time}:00`
  // no hours in the beginning
  if (time.length === 5) time = `00:${time}`
  // deal with time input without leading zeros, like 1:32 or 3:44:55
  if (time.length === 4 && time.charAt(0) !== "0") time = `00:0${time}`
  if (time.length === 7 && time.charAt(1) !== "0") time = `0${time}`
  // split time into hours, minutes, seconds
  const [hours, minutes, seconds] = time.split(":")
  // parseInt does not care about leading zeroes
  return parseInt(hours) * 60 * 60 + parseInt(minutes) * 60 + parseInt(seconds)
}

// Method to convert seconds to HH:MM:SS
export const convertToTime = (
  seconds: number,
  ignoreHours: boolean = false,
): string => {
  const hours = Math.floor(seconds / 60 / 60)
  const minutes = Math.floor((seconds - hours * 60 * 60) / 60)
  const secs = seconds - hours * 60 * 60 - minutes * 60
  // zero pad hours, minutes, seconds
  const hoursStr = hours.toString().padStart(2, "0")
  const minutesStr = minutes.toString().padStart(2, "0")
  const secondsStr = secs.toString().padStart(2, "0")
  if (ignoreHours) {
    return `${minutesStr}:${secondsStr}`
  }
  return `${hoursStr}:${minutesStr}:${secondsStr}`
}

/**
 * Normalize elapsedTime to HH:MM:SS format.
 * Handles various input formats (MM:SS, M:SS, HH:MM:SS) and ensures
 * the output is always in HH:MM:SS format for MySQL TIME column compatibility.
 */
export const normalizeElapsedTime = (time: string | undefined): string => {
  if (!time || time === "") return "00:00:00"
  const seconds = convertToSeconds(time)
  return convertToTime(seconds)
}

export const arrayHasId = (array: Event[], id: number): boolean => {
  for (const item of array) {
    if (item.id === id) return true
  }
  return false
}

export const simContents = (
  sim: GetUserapiAdminTestsGetTestByIdByTestIdApiResponse,
): Contents => {
  const data = sim?.data
  if (!data || !data[0] || !data[0].contents) return {} as Contents
  return JSON.parse(data[0].contents)
}

export const extractEvents = (
  sim: GetUserapiAdminTestsGetTestByIdByTestIdApiResponse,
) => {
  const events = [] as Event[]
  const contents = simContents(sim)
  if (!contents) return events

  const { plots } = contents?.map || {}
  const allEvents: Event[] = []
  for (const cell in plots) {
    const { events } = plots[cell] || {}
    events.forEach((event: Event) => {
      const {
        id,
        description,
        elapsedTime,
        response,
        action,
        validateResponse,
        categoryId,
        type,
      } = event || {}
      const { name } = event || {}
      // name = name?.toLowerCase().indexOf("message") === 0 ? "Message" : ""
      const arr = name ? name.match(/\d+/) : []
      const titleCount = arr && arr[0] ? parseInt(arr[0]) : ""
      allEvents.push({
        cell: cell,
        eventId: id,
        title: `MESSAGE ${titleCount}`,
        description,
        date: getDate(),
        elapsedTime: normalizeElapsedTime(elapsedTime),
        response,
        action,
        validateResponse,
        id,
        categoryId,
        currentDate: moment(new Date()).format("YYYY-MM-DD hh:mm:ss"),
        messageType: type,
        showResponse: false,
        planId: undefined,
        decisionId: undefined,
        icon: "",
        type: "message",
        mode: "message",
        reasonIds: [],
        checked: false,
      })
    })
  }
  allEvents.sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
  return allEvents
}

export const extractInformation = (
  sim: GetUserapiAdminTestsGetTestByIdByTestIdApiResponse,
) => {
  const information: Information[] = []
  const contents = simContents(sim)
  if (!contents) return information
  for (const info of contents.actionset.categories) {
    if (info.text === "Information Search") information.push(info)
  }
  return information
}

export const extractChoices = (
  sim: GetUserapiAdminTestsGetTestByIdByTestIdApiResponse,
) => {
  const information: Information[] = []
  const contents = simContents(sim)
  if (!contents) return information
  for (const info of contents.actionset.categories) {
    if (info.text !== "Information Search") information.push(info)
  }
  return information
}

// Helper functions to generate descriptive sentences when actionSentence/planSentence is missing
const generateActionSentence = (text: string): string => {
  return `Your decision to "${text}" is being implemented by your team.`
}

const generatePlanSentence = (text: string): string => {
  return `Your plan to "${text}" is ready to be implemented by your team.`
}

// Create a dictionary to store the details
export const ChoiceTypeDetails: Record<
  ChoiceType,
  { type: string; title: string; mode: string }
> = {
  [ChoiceType.decision]: {
    type: "decision",
    title: "decision",
    mode: "decision",
  },
  [ChoiceType.plan]: {
    type: "cPlan",
    title: "Plan created",
    mode: "cPlan",
  },
  [ChoiceType.cplan]: {
    type: "cPlan",
    title: "Plan created",
    mode: "cPlan",
  },
  [ChoiceType.eplan]: {
    type: "ePlan",
    title: "Plan executed",
    mode: "ePlan",
  },
  [ChoiceType.search]: {
    type: "message",
    title: "INFORMATION SEARCH",
    mode: "information",
  },
  [ChoiceType.add]: {
    type: "add",
    title: "Add",
    mode: "add",
  },
}

export const choiceToEvent = (
  choice: Choice,
  secondsElapsed: number,
  sortedItems: InformationType[],
  crisisStatus: CrisisStatus,
): Event => {
  const choiceType = choice.choiceType
  const item = choice.item

  let response: Response
  if (item.responses && (item.responses.length || 0) >= 1) {
    response = item.responses[crisisStatus] ||
      item.responses[0] || { response: "" }
  } else {
    response = { response: "" } as Response
  }

  let event:
    | InformationWithExtraType
    | DecisionWithExtraType
    | MessagesWithExtraType = {
    decisionId: 0,
    planId: 0,
    measureReference: "",
    categoryId: 0,
    checked: false,
    date: "",
    description: "",
    elapsedTime: "",
    id: 0,
    mode: "",
    reasonIds: [],
    text: "",
    title: "",
    type: "",
  }
  // Need to do a case switch here for ChoiceType
  let description = ""
  switch (choiceType) {
    case ChoiceType.decision:
      description = item.actionSentence || generateActionSentence(item.text)
      event.decisionId = sortedItems.length === 0 ? 1 : sortedItems.length + 1
      event.planId = 0
      event.measureReference = ""
      event.categoryId = item.categoryId || null
      event.text = item.text
      break
    case ChoiceType.eplan:
      description = item.actionSentence || generateActionSentence(item.text)
      event.decisionId = 0
      event.measureReference = ""
      event.categoryId = item.categoryId || null
      event.text = item.text
      break
    case ChoiceType.plan:
      description = item.planSentence || generatePlanSentence(item.text)
      event.decisionId = 0
      event.planId = sortedItems.length === 0 ? 1 : sortedItems.length + 1
      event.measureReference = ""
      event.categoryId = item.categoryId || null
      event.text = item.text
      event.order = item.order
      break
    case ChoiceType.search:
      description = response.response
      if ("parentMessage" in event) event.parentMessage = ""
      event.response = response.response
      event.categoryId = item.categoryId || null
      break
    default:
      break
  }
  // Handle dateTime - use choice.time if valid, otherwise use current time
  let dateTime: string
  try {
    const timeValue = choice.time ?? Date.now()
    const date = new Date(timeValue)
    // Check if the date is valid
    dateTime = isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString()
  } catch {
    dateTime = new Date().toISOString()
  }

  event = {
    ...event,
    ...ChoiceTypeDetails[choiceType],
    id: choice.item.id,
    elapsedTime: convertToTime(secondsElapsed),
    dateTime,
    description: description || "",
    // mode: choice.choiceType,
    connections: choice.connections as SavePointLogType[],
    reasonIds: choice?.connections
      ? choice?.connections
          .filter(
            (connection) =>
              connection !== undefined && connection.id !== undefined,
          )
          .map((connection) => connection.id as number)
      : [],
    // title: convertToCamelCase(choice.choiceType),
    date: getDate(),
    checked: false,
  }
  return event
}

export const getMessageType = (event: Event): MessageTypes => {
  switch (event.type?.toLowerCase()) {
    case "n":
      return MessageTypes.INCOMING
    case "decision":
    case "plan_executed":
      return MessageTypes.DECISION
    case "message":
      if (event.mode === "information") {
        return MessageTypes.INFO_SEARCH
      }
      if (event.mode === "fact_check") {
        return MessageTypes.FACT_CHECK
      }
      return MessageTypes.INCOMING
    case "search":
      return MessageTypes.INFO_SEARCH
    case "plan":
      return MessageTypes.PLAN
    case "cplan":
      return MessageTypes.PLAN
    case "eplan":
      return MessageTypes.PLAN
    default:
      return MessageTypes.INCOMING
  }
}

export const urlHasResetTrigger = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search)
  const resetState = urlParams.get("r")
  return resetState === "true"
}

export const urlHasTimeReset = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search)
  const resetTime = urlParams.get("t")
  if (!resetTime) return false
  return resetTime.length > 0
}

export const parseTimeFromUrl = (): number => {
  const urlParams = new URLSearchParams(window.location.search)
  const time = urlParams.get("t")
  if (!time) return 0
  return convertToSeconds(time)
}

export const resetSimulationState = (
  dispatch: Dispatch,
  reload: boolean = true,
) => {
  dispatch(destroyTimer())
  dispatch(resetSimulation())
  dispatch(resetDecisions())
  storage.removeItem("persist:msk:simulation")
  if (urlHasResetTrigger()) {
    // remove all params from current url
    window.history.replaceState({}, "", window.location.pathname)
  }
  // reload page
  if (reload) window.location.reload()
}
export const convertToCamelCase = (inputString: string) => {
  return inputString.replace(/_([a-z])/g, (_, char) => char.toUpperCase())
}
export const getDate = () => {
  return moment(new Date()).utc().format("YYYY-MM-DD hh:mm:ss")
}

export const processSimulations = (simulations: Simulation[] | undefined) => {
  if (!simulations || simulations.length === 0) {
    return []
  }

  return simulations.map((simulation: Simulation) => {
    let parsedContents: Contents | string

    if (typeof simulation?.contents === "string") {
      parsedContents = JSON.parse(simulation?.contents)
    } else {
      parsedContents = simulation?.contents || ""
    }

    return {
      ...simulation,
      imgUrl:
        parsedContents &&
        typeof parsedContents === "object" &&
        parsedContents.map &&
        parsedContents.map.image &&
        parsedContents.map.image.ref
          ? environment.imageUrl + parsedContents.map.image.ref
          : "",
      contents: parsedContents,
    }
  })
}

export const getLocalSimLogo = (name: string | undefined) => {
  return simLogos[name || ""]
}

export const getSimRunId = (params: Readonly<Params<string>>) => {
  const runIdStr = params.runId

  // Team simulations don't have runId - this is expected behavior, not an error
  if (!runIdStr) {
    return 0
  }

  const runId = parseInt(runIdStr)
  if (isNaN(runId)) {
    console.error(`runId is not a number: ${runIdStr}`)
    return 0
  }
  return runId
}

export const enum CrisisStatus {
  PRE_CRISIS = 0,
  CRISIS = 1,
  POST_CRISIS = 2,
}

export const currentCrisisStatus = (state: SimulationState) => {
  const { emergencyStart, emergencyEnd, secondsElapsed } = state
  if (!emergencyStart || !emergencyEnd) return CrisisStatus.PRE_CRISIS
  const emergencyStartSeconds = convertToSeconds(emergencyStart)
  const emergencyEndSeconds = convertToSeconds(emergencyEnd)
  if (secondsElapsed < emergencyStartSeconds) return CrisisStatus.PRE_CRISIS
  if (
    secondsElapsed >= emergencyStartSeconds &&
    secondsElapsed < emergencyEndSeconds
  )
    return CrisisStatus.CRISIS
  return CrisisStatus.POST_CRISIS
}
